import React, { useMemo, useState } from "react";
import type { Category } from "../API/Models";

type CategoryPathOption = {
	key: string;
	ids: string[];
	label: string;
};

type CategoryPathSelectProps = {
	categories: Category[];
	value: string[];
	onChange: (next: string[]) => void;
	label?: string;
	placeholder?: string;
	helper?: string;
	disabled?: boolean;
};

const buildCategoryPaths = (categories: Category[]): CategoryPathOption[] => {
	const map = new Map<string, Category>();
	categories.forEach((node) => {
		if (node.id && node.title) {
			map.set(node.id, node);
		}
	});

	const options: CategoryPathOption[] = [];
	map.forEach((node) => {
		const ids: string[] = [];
		const visited = new Set<string>();
		let cursor: Category | undefined = node;
		while (cursor?.id) {
			if (visited.has(cursor.id)) break;
			visited.add(cursor.id);
			ids.push(cursor.id);
			cursor = cursor.parentId ? map.get(cursor.parentId) : undefined;
		}
		ids.reverse();
		if (ids.length === 0) return;
		const label = ids
			.map((id) => map.get(id)?.title ?? id)
			.filter(Boolean)
			.join(" > ");
		const key = ids.join("/");
		options.push({ key, ids, label });
	});

	return options.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
};

/** Builds a breadcrumb-style label (e.g. "Parent > Child") from a list of category path IDs. */
export const buildCategoryPathLabel = (categories: Category[], pathIds: string[]) => {
	const map = new Map(categories.filter((node) => node.id).map((node) => [node.id as string, node.title ?? node.id]));
	return (pathIds ?? []).map((id) => map.get(id) ?? id).join(" > ");
};

/** Dropdown select for choosing a full category path, displaying breadcrumb labels with search filtering. */
const CategoryPathSelect: React.FC<CategoryPathSelectProps> = ({
	categories,
	value,
	onChange,
	label = "Category",
	placeholder = "Select category path",
	helper,
	disabled
}) => {
	const [query, setQuery] = useState("");
	const [isFocused, setIsFocused] = useState(false);
	const options = useMemo(() => buildCategoryPaths(categories ?? []), [categories]);
	const selectedLabel = useMemo(
		() => buildCategoryPathLabel(categories ?? [], value ?? []),
		[categories, value]
	);
	const filteredOptions = useMemo(() => {
		const term = query.trim().toLowerCase();
		if (!term) return options;
		return options.filter((option) => option.label.toLowerCase().includes(term));
	}, [options, query]);
	const showList = isFocused && !disabled;

	return (
		<div className="relative" onFocusCapture={() => setIsFocused(true)} onBlurCapture={() => setIsFocused(false)}>
			<label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</label>
			<div className="mt-1 flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
				<input
					type="text"
					value={query}
					onChange={(event) => setQuery(event.target.value)}
					placeholder={placeholder}
					disabled={disabled}
					className="flex-1 min-w-[160px] border-none text-sm focus:outline-none"
				/>
				{selectedLabel && (
					<span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
						{selectedLabel}
					</span>
				)}
				{value?.length > 0 && !disabled && (
					<button
						type="button"
						onClick={() => onChange([])}
						className="text-xs font-semibold text-slate-500 hover:text-slate-700"
					>
						Clear
					</button>
				)}
			</div>
			{helper && <p className="mt-1 text-xs text-slate-400">{helper}</p>}
			{showList && (
				<div className="absolute left-0 right-0 z-50 mt-2 max-h-64 overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg">
					{filteredOptions.length === 0 ? (
						<p className="px-3 py-2 text-xs text-slate-500">No categories match your search.</p>
					) : (
						filteredOptions.map((option) => (
							<button
								key={option.key}
								type="button"
								onMouseDown={(event) => event.preventDefault()}
								onClick={() => {
									onChange(option.ids);
									setQuery("");
								}}
								className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
							>
								{option.label}
							</button>
						))
					)}
				</div>
			)}
		</div>
	);
};

export default CategoryPathSelect;
